import { Editor, findParentNode } from '@tiptap/core';
import { Node as ProseMirrorNode } from '@tiptap/pm/model';
import { type EditorState, TextSelection } from '@tiptap/pm/state';
import { useEditorState } from '@tiptap/react';
// `@tiptap/react/menus` is a v3 `exports`-only subpath that the repo's classic
// `moduleResolution: node` (consumer lib type-check) can't resolve — Vite and the
// bundler tsc both can. Mirrors the editor's own bubble menu
// (eluve-editor/.../bubble-menu). @ts-ignore (not @ts-expect-error) because under
// bundler resolution the import succeeds and there'd be no error to "expect".
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import { BubbleMenu } from '@tiptap/react/menus';
import React from 'react';

import { Icon, IconName, cn } from '@eluve/components';
import { useTranslation } from '@eluve/i18n';

import { TemplateSectionAttributes } from './TemplateSectionNode';

const findSection = (editor: Editor) =>
  findParentNode((node) => node.type.name === 'templateSection')(
    editor.state.selection,
  );

const findGroup = (editor: Editor) =>
  findParentNode((node) => node.type.name === 'templateGroup')(
    editor.state.selection,
  );

type NodeFinder = typeof findSection;

/**
 * Swaps the found node with its previous/next sibling (sections move
 * within their parent — the doc or a group; groups move within the
 * doc). The caret is restored into the moved node's title so the
 * bubble menu stays open for repeated nudges.
 */
const moveNode = (editor: Editor, finder: NodeFinder, direction: -1 | 1) => {
  const current = finder(editor);
  if (!current) {
    return;
  }
  const { pos, node } = current;
  const $pos = editor.state.doc.resolve(pos);
  const parent = $pos.parent;
  const index = $pos.index();
  const siblingIndex = index + direction;
  if (siblingIndex < 0 || siblingIndex >= parent.childCount) {
    return;
  }
  const sibling = parent.child(siblingIndex);

  const from = direction === -1 ? pos - sibling.nodeSize : pos;
  const to = from + sibling.nodeSize + node.nodeSize;
  const transaction = editor.state.tr.replaceWith(
    from,
    to,
    direction === -1 ? [node, sibling] : [sibling, node],
  );
  // Caret onto the moved node's title (+2: into the node, into its title)
  const nodeStart = direction === -1 ? from : from + sibling.nodeSize;
  transaction.setSelection(
    TextSelection.near(transaction.doc.resolve(nodeStart + 2)),
  );
  transaction.scrollIntoView();
  editor.view.dispatch(transaction);
  editor.view.focus();
};

/** Whether the found node has a sibling in the given direction */
const canMove = (editor: Editor, finder: NodeFinder, direction: -1 | 1) => {
  const current = finder(editor);
  if (!current) {
    return false;
  }
  const $pos = editor.state.doc.resolve(current.pos);
  const siblingIndex = $pos.index() + direction;
  return siblingIndex >= 0 && siblingIndex < $pos.parent.childCount;
};

// Private Components

/** Matches the toggle-button idiom of the rich text editor bubble menu */
const MenuToggleButton: React.FC<{
  icon: IconName;
  label?: string;
  ariaLabel?: string;
  isActive?: boolean;
  onClick: () => void;
}> = ({ icon, label, ariaLabel, isActive = false, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    // Without this, mousedown on the button clears the editor selection
    // before the click handler runs, so commands apply to a collapsed caret
    onMouseDown={(e) => e.preventDefault()}
    aria-pressed={isActive}
    aria-label={ariaLabel ?? label}
    className="flex items-center gap-1.5 p-2 text-text-tertiary hover:bg-bg-tertiary active:bg-border-primary"
  >
    <Icon
      name={icon}
      size="md"
      className={cn({ 'text-bg-accent-strong-primary': isActive })}
    />
    {label && (
      <span
        className={cn('text-sm font-medium', {
          'text-bg-accent-strong-primary': isActive,
        })}
      >
        {label}
      </span>
    )}
  </button>
);

/** Up/down nudge buttons shared by section and group controls */
const MoveControls: React.FC<{
  editor: Editor;
  finder: NodeFinder;
  what: 'section' | 'group';
}> = ({ editor, finder, what }) => {
  const { t } = useTranslation();
  return (
    <>
      <button
        type="button"
        onClick={() => moveNode(editor, finder, -1)}
        onMouseDown={(e) => e.preventDefault()}
        disabled={!canMove(editor, finder, -1)}
        aria-label={
          what === 'section'
            ? t('outputTemplates.bubbleMenu.moveSectionUp')
            : t('outputTemplates.bubbleMenu.moveGroupUp')
        }
        className="p-2 text-text-tertiary hover:bg-bg-tertiary active:bg-border-primary disabled:pointer-events-none disabled:opacity-40"
      >
        <Icon name="ArrowUp" size="md" />
      </button>
      <button
        type="button"
        onClick={() => moveNode(editor, finder, 1)}
        onMouseDown={(e) => e.preventDefault()}
        disabled={!canMove(editor, finder, 1)}
        aria-label={
          what === 'section'
            ? t('outputTemplates.bubbleMenu.moveSectionDown')
            : t('outputTemplates.bubbleMenu.moveGroupDown')
        }
        className="p-2 text-text-tertiary hover:bg-bg-tertiary active:bg-border-primary disabled:pointer-events-none disabled:opacity-40"
      >
        <Icon name="ArrowDown" size="md" />
      </button>
    </>
  );
};

/** Section-level controls, shown when the caret is on a section title */
const SectionControls: React.FC<{ editor: Editor }> = ({ editor }) => {
  const { t } = useTranslation();
  // TipTap v3 no longer re-renders this component on every transaction the
  // way v2 did, so the section attrs must be read through useEditorState.
  // Reading node.attrs directly at render leaves them stale after the first
  // toggle, so the next click recomputes the same target (e.g. Required
  // turns off once and never back on).
  const attrs = useEditorState({
    editor,
    selector: ({ editor: currentEditor }): TemplateSectionAttributes => {
      const found = findSection(currentEditor);
      const sectionAttrs = (found?.node.attrs ??
        {}) as Partial<TemplateSectionAttributes>;
      return {
        sectionId: sectionAttrs.sectionId ?? null,
        isRequired: sectionAttrs.isRequired ?? false,
        isAiEnabled: sectionAttrs.isAiEnabled ?? true,
      };
    },
  });

  const updateSection = (patch: Partial<TemplateSectionAttributes>) => {
    const current = findSection(editor);
    if (!current) {
      return;
    }
    const transaction = editor.state.tr.setNodeMarkup(current.pos, undefined, {
      ...current.node.attrs,
      ...patch,
    });
    editor.view.dispatch(transaction);
    // Keep focus in the editor so the menu stays open after toggling
    editor.view.focus();
  };

  const deleteSection = () => {
    const current = findSection(editor);
    if (!current) {
      return;
    }
    editor
      .chain()
      .focus()
      .deleteRange({
        from: current.pos,
        to: current.pos + current.node.nodeSize,
      })
      .run();
  };

  return (
    <>
      <MenuToggleButton
        icon={attrs.isAiEnabled ? 'WandSparkles' : 'Pencil'}
        label={
          attrs.isAiEnabled
            ? t('outputTemplates.bubbleMenu.generateUsingAi')
            : t('outputTemplates.bubbleMenu.writeManually')
        }
        isActive={attrs.isAiEnabled}
        onClick={() => updateSection({ isAiEnabled: !attrs.isAiEnabled })}
      />
      <MenuToggleButton
        icon="CircleAlert"
        label={t('outputTemplates.editor.required')}
        isActive={attrs.isRequired}
        onClick={() => updateSection({ isRequired: !attrs.isRequired })}
      />
      <MoveControls editor={editor} finder={findSection} what="section" />
      <button
        type="button"
        onClick={deleteSection}
        onMouseDown={(e) => e.preventDefault()}
        aria-label={t('outputTemplates.bubbleMenu.deleteSection')}
        className="p-2 text-text-tertiary hover:bg-bg-tertiary active:bg-border-primary"
      >
        <Icon name="Trash2" size="md" />
      </button>
    </>
  );
};

/** Group-level controls, shown when the caret is on a group name */
const GroupControls: React.FC<{ editor: Editor }> = ({ editor }) => {
  const { t } = useTranslation();
  const ungroup = () => {
    const current = findGroup(editor);
    if (!current) {
      return;
    }
    // Replace the group with its child sections (the name is dropped)
    const sections: ProseMirrorNode[] = [];
    current.node.forEach((child) => {
      if (child.type.name === 'templateSection') {
        sections.push(child);
      }
    });
    const transaction = editor.state.tr.replaceWith(
      current.pos,
      current.pos + current.node.nodeSize,
      sections,
    );
    editor.view.dispatch(transaction);
    editor.view.focus();
  };

  const deleteGroup = () => {
    const current = findGroup(editor);
    if (!current) {
      return;
    }
    editor
      .chain()
      .focus()
      .deleteRange({
        from: current.pos,
        to: current.pos + current.node.nodeSize,
      })
      .run();
  };

  return (
    <>
      <MoveControls editor={editor} finder={findGroup} what="group" />
      <button
        type="button"
        onClick={ungroup}
        onMouseDown={(e) => e.preventDefault()}
        className="flex items-center gap-1.5 p-2 text-text-tertiary hover:bg-bg-tertiary active:bg-border-primary"
      >
        <span className="text-sm font-medium">
          {t('outputTemplates.bubbleMenu.ungroup')}
        </span>
      </button>
      <button
        type="button"
        onClick={deleteGroup}
        onMouseDown={(e) => e.preventDefault()}
        aria-label={t('outputTemplates.bubbleMenu.deleteGroupAria')}
        className="p-2 text-text-tertiary hover:bg-bg-tertiary active:bg-border-primary"
      >
        <Icon name="Trash2" size="md" />
      </button>
    </>
  );
};

/**
 * Markdown formatting controls, shown on a body text selection. These
 * map 1:1 onto markdown constructs since the template ultimately ships
 * to the LLM as markdown: headings, normal text, bold/italic, and
 * bullet/numbered lists.
 */
const FormattingControls: React.FC<{ editor: Editor }> = ({ editor }) => {
  const { t } = useTranslation();
  return (
    <>
      <MenuToggleButton
        icon="Heading2"
        ariaLabel={t('outputTemplates.bubbleMenu.heading')}
        isActive={editor.isActive('heading', { level: 2 })}
        onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
      />
      <MenuToggleButton
        icon="Heading3"
        ariaLabel={t('outputTemplates.bubbleMenu.subheading')}
        isActive={editor.isActive('heading', { level: 3 })}
        onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
      />
      <MenuToggleButton
        icon="Type"
        ariaLabel={t('outputTemplates.bubbleMenu.normalText')}
        isActive={editor.isActive('paragraph')}
        onClick={() => editor.chain().focus().setParagraph().run()}
      />
      <MenuToggleButton
        icon="Bold"
        ariaLabel={t('outputTemplates.bubbleMenu.bold')}
        isActive={editor.isActive('bold')}
        onClick={() => editor.chain().focus().toggleBold().run()}
      />
      <MenuToggleButton
        icon="Italic"
        ariaLabel={t('outputTemplates.bubbleMenu.italic')}
        isActive={editor.isActive('italic')}
        onClick={() => editor.chain().focus().toggleItalic().run()}
      />
      <MenuToggleButton
        icon="List"
        ariaLabel={t('outputTemplates.bubbleMenu.bulletList')}
        isActive={editor.isActive('bulletList')}
        onClick={() => editor.chain().focus().toggleBulletList().run()}
      />
      <MenuToggleButton
        icon="ListOrdered"
        ariaLabel={t('outputTemplates.bubbleMenu.numberedList')}
        isActive={editor.isActive('orderedList')}
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
      />
    </>
  );
};

// Public Components

export interface SectionBubbleMenuProps {
  editor: Editor;
}

/**
 * Contextual bubble menu for the section editor. On a section title it
 * offers section-level controls (AI / required / delete); on a body
 * text selection it offers markdown formatting (headings, normal text,
 * bold, italic, lists). Follows the toggle-button styling of the
 * shared editor bubble menu.
 */
export const SectionBubbleMenu: React.FC<SectionBubbleMenuProps> = ({
  editor,
}) => {
  // Which controls to show must come through useEditorState: TipTap v3
  // doesn't re-render this component on selection changes, so reading
  // editor.isActive(...) at render goes stale — the formatting bar would
  // linger when the caret moves from a body selection into a section/group
  // title (it must only show while editing the body). Same reason
  // SectionControls reads its attrs via useEditorState.
  const context = useEditorState({
    editor,
    selector: ({ editor: currentEditor }): 'group' | 'section' | 'body' => {
      if (currentEditor.isActive('groupTitle')) {
        return 'group';
      }
      if (currentEditor.isActive('sectionTitle')) {
        return 'section';
      }
      return 'body';
    },
  });

  return (
    <BubbleMenu
      editor={editor}
      shouldShow={({
        editor: currentEditor,
        state,
      }: {
        editor: Editor;
        state: EditorState;
      }) => {
        // isFocused keeps spurious update-cycle re-shows from resurfacing
        // the menu after focus has moved elsewhere (e.g. into the settings
        // panel) — the selection, and therefore isActive, persists across
        // blur. Menu buttons preventDefault on mousedown, so interacting
        // with the menu never unfocuses the editor.
        if (!currentEditor.isEditable || !currentEditor.isFocused) {
          return false;
        }
        if (
          currentEditor.isActive('sectionTitle') ||
          currentEditor.isActive('groupTitle')
        ) {
          return true;
        }
        return (
          !state.selection.empty && currentEditor.isActive('templateSection')
        );
      }}
      options={{ placement: 'top-start' }}
      className="flex w-fit divide-x divide-border-primary rounded-sm border border-border-primary bg-white shadow-xl"
    >
      {context === 'group' ? (
        <GroupControls editor={editor} />
      ) : context === 'section' ? (
        <SectionControls editor={editor} />
      ) : (
        <FormattingControls editor={editor} />
      )}
    </BubbleMenu>
  );
};
